<template>
  <div class="min-h-screen bg-white dark:bg-gray-950 font-sans antialiased" :class="{ 'dark': isDarkMode }">

    <!-- Header -->
    <header class="sticky top-0 z-50 bg-white/80 dark:bg-gray-950/80 backdrop-blur-sm border-b border-zinc-100 dark:border-zinc-800">
      <div class="px-8 h-14 flex items-center justify-between">
        <router-link to="/" class="flex items-center no-underline">
          <img :src="logoImage" alt="Logo" class="h-7 w-auto" />
        </router-link>
        <div class="flex items-center gap-3">
          <button @click="savePersonalInfo" :disabled="isSaving" class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity">
            <svg v-if="isSaving" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
            </svg>
            <span>{{ isSaving ? 'Saving…' : 'Save Profile' }}</span>
          </button>
          <UserDropdown
            :current-user="currentUser || {}"
            @logout="handleLogout"
          />
        </div>
      </div>
    </header>

    <!-- Welcome banner -->
    <div v-if="showWelcomeBanner" class="bg-green-50 dark:bg-green-950 border-b border-green-200 dark:border-green-800">
      <div class="max-w-screen-xl mx-auto px-8 py-2.5 flex items-center gap-3">
        <svg class="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
        </svg>
        <span class="flex-1 text-sm text-green-700 dark:text-green-300">Your CV data is pre-filled below. Review it, then click <strong>Save Profile</strong> to go to your dashboard.</span>
        <button @click="showWelcomeBanner = false" class="text-green-500 hover:text-green-700 transition-colors">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
    </div>

    <!-- Toast messages -->
    <div v-if="successMessage" class="fixed top-16 right-4 z-50 flex items-center gap-2 px-4 py-3 bg-white dark:bg-gray-950 border border-zinc-100 dark:border-zinc-800 rounded-xl text-sm text-zinc-700 dark:text-zinc-300 max-w-sm">
      <svg class="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
      {{ successMessage }}
    </div>
    <div v-if="errorMessage" class="fixed top-16 right-4 z-50 flex items-center gap-2 px-4 py-3 bg-white dark:bg-gray-950 border border-red-100 dark:border-red-900 rounded-xl text-sm text-red-600 max-w-sm">
      <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
      {{ errorMessage }}
    </div>

    <!-- Unsaved changes modal -->
    <UnsavedChangesModal
      :is-open="showUnsavedChangesModal"
      :is-saving="isSaving"
      @close="hideUnsavedChangesModal"
      @discard="discardChangesAndLeave"
      @save-and-leave="saveAndLeave"
    />

    <!-- Upload modal -->
    <div v-if="showUploadModal" class="fixed inset-0 z-50 flex items-center justify-center p-4" style="background:rgba(0,0,0,0.2);backdrop-filter:blur(4px)" @click.self="closeUploadModal">
      <div class="bg-white dark:bg-gray-950 border border-zinc-100 dark:border-zinc-800 rounded-2xl w-full max-w-xl overflow-hidden">
        <div class="flex items-center justify-between px-6 pt-5 pb-4 border-b border-zinc-100 dark:border-zinc-800">
          <h3 class="text-base font-semibold text-zinc-900 dark:text-white">Upload Your CV</h3>
          <button @click="closeUploadModal" class="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
        <div class="px-6 py-5">
          <div @dragover.prevent @drop="handleDrop" class="border border-dashed border-zinc-200 dark:border-zinc-700 rounded-xl p-8 text-center hover:border-primary dark:hover:border-primary transition-colors">
            <div v-if="!uploadedFile" class="flex flex-col items-center gap-4">
              <svg class="w-10 h-10 text-zinc-300 dark:text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/></svg>
              <div>
                <p class="text-sm font-medium text-zinc-900 dark:text-white">Drop your CV here or click to browse</p>
                <p class="text-xs text-zinc-400 mt-1">PDF, DOC, DOCX, JPG, PNG (max 5 MB · up to 5 pages)</p>
              </div>
              <input type="file" @change="handleFileUpload" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt" class="hidden" id="cv-upload-profile" />
              <label for="cv-upload-profile" class="px-3 py-1.5 border border-zinc-100 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400 text-xs font-medium rounded-lg hover:border-primary transition-colors cursor-pointer">Choose File</label>
            </div>
            <div v-else class="flex flex-col items-center gap-3">
              <svg class="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
              <p class="text-sm font-medium text-zinc-900 dark:text-white">{{ uploadedFile.name }}</p>
              <button @click="uploadedFile = null" class="text-xs text-zinc-400 hover:text-zinc-600 transition-colors">Choose different file</button>
            </div>
          </div>
          <div v-if="isUploading" class="flex flex-col items-center gap-3 pt-4">
            <div class="w-5 h-5 border-2 border-zinc-100 border-t-primary rounded-full animate-spin"></div>
            <p class="text-sm text-zinc-500">{{ uploadProgress }}</p>
          </div>
        </div>
        <div class="flex items-center justify-end gap-2 px-6 py-4 border-t border-zinc-100 dark:border-zinc-800">
          <button @click="closeUploadModal" class="px-4 py-2 text-sm font-medium text-zinc-500 border border-zinc-100 rounded-lg hover:border-zinc-200 transition-colors">Cancel</button>
          <button @click="uploadAndPopulateProfile" :disabled="!uploadedFile || isUploading" class="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity">
            {{ isUploading ? 'Processing…' : 'Upload & Populate' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <main class="max-w-screen-xl mx-auto px-6 py-8">

      <!-- Page header -->
      <div class="mb-8">
        <router-link to="/dashboard" class="text-xs text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors no-underline">← Dashboard</router-link>
        <h1 class="text-xl font-semibold text-zinc-900 dark:text-white mt-3">My Profile</h1>
        <p class="text-sm text-zinc-400 dark:text-zinc-500 mt-0.5">Edit once, use across all your CVs</p>
      </div>

      <!-- Upload banner -->
      <div v-if="isProfileEmpty && showUploadBanner" class="border border-gold-200 dark:border-gold-800/40 bg-gold-50/50 dark:bg-gold-900/10 rounded-xl p-5 mb-8">
        <div class="flex items-center gap-4">
          <svg class="w-7 h-7 text-gold flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/></svg>
          <div class="flex-1">
            <h3 class="text-sm font-semibold text-zinc-900 dark:text-white">Quick Start: Upload Your Existing CV</h3>
            <p class="text-xs text-zinc-500 mt-0.5">Save time by uploading your current CV — we'll extract your information automatically.</p>
          </div>
          <div class="flex items-center gap-3 flex-shrink-0">
            <button @click="showUploadModal = true" class="px-3 py-1.5 text-xs font-medium text-white bg-primary rounded-lg hover:opacity-90 transition-opacity">Upload CV</button>
            <button @click="showUploadBanner = false" class="text-xs text-zinc-400 hover:text-zinc-600 transition-colors">Skip</button>
          </div>
        </div>
      </div>

      <!-- Two-column layout -->
      <div class="flex gap-10 items-start">

        <!-- Sticky section nav -->
        <nav class="w-40 flex-shrink-0 sticky top-20 flex flex-col gap-0.5">
          <a
            v-for="item in navItems"
            :key="item.id"
            :href="'#' + item.id"
            @click.prevent="scrollToSection(item.id)"
            class="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors no-underline cursor-pointer"
            :class="activeSection === item.id
              ? 'text-primary dark:text-primary bg-zinc-50 dark:bg-zinc-800/50'
              : 'text-zinc-400 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800/50'"
          >
            <span class="flex-shrink-0 opacity-70 w-3.5 h-3.5 flex items-center" v-html="item.icon"></span>
            {{ item.label }}
          </a>
        </nav>

        <!-- Sections -->
        <div class="flex-1 min-w-0 flex flex-col gap-10">

          <!-- Personal Info -->
          <section id="section-personal" class="profile-section-anchor">
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-2">
                <span class="w-0.5 h-3.5 rounded-full bg-gold flex-shrink-0"></span>
                <span class="text-xs font-semibold uppercase tracking-widest text-zinc-700 dark:text-zinc-300">Personal Info</span>
              </div>
            </div>
            <PersonalInfoForm
              :personal-info="personalInfo"
              :highlighted-fields="highlightedFields.personalInfo"
              @update:personal-info="personalInfo = $event"
              @field-changed="handlePersonalInfoChange"
            />
          </section>

          <!-- Experience -->
          <section id="section-experience" class="profile-section-anchor">
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-2">
                <span class="w-0.5 h-3.5 rounded-full bg-gold flex-shrink-0"></span>
                <span class="text-xs font-semibold uppercase tracking-widest text-zinc-700 dark:text-zinc-300">Experience</span>
              </div>
              <button @click="openNew('experience')" class="text-xs font-medium text-primary dark:text-primary-300 hover:opacity-70 transition-opacity">+ Add</button>
            </div>
            <div v-if="experienceEntries.length === 0 && !isLoadingEntries" class="text-sm text-zinc-400 dark:text-zinc-500 py-4">No experience entries yet.</div>
            <div class="flex flex-col gap-4">
              <div v-for="entry in experienceEntries" :key="entry.id" class="flex flex-col gap-2 w-full">
                <div class="flex gap-3 items-stretch w-full">
                  <ProfileEntryCard
                    entry-type="experience"
                    :entry="entry"
                    @edit="openEdit('experience', entry)"
                    @archive="archiveEntry('experience', entry)"
                  />
                  <ProfileEntryCard
                    v-for="variant in (entry.variants || [])"
                    :key="variant.id"
                    entry-type="experience"
                    :entry="variant"
                    @edit="openEdit('experience', variant)"
                    @archive="archiveVariant('experience', entry, variant)"
                  />
                  <button @click="openVariant('experience', entry)" class="add-variant-btn">
                    <svg class="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
                    <span class="add-variant-label">add alternative</span>
                  </button>
                </div>
                <MasterReferenceSection
                  entry-type="experience"
                  :entry-id="entry.id"
                  :has-variants="(entry.variants || []).length > 0"
                />
              </div>
            </div>
          </section>

          <!-- Education -->
          <section id="section-education" class="profile-section-anchor">
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-2">
                <span class="w-0.5 h-3.5 rounded-full bg-gold flex-shrink-0"></span>
                <span class="text-xs font-semibold uppercase tracking-widest text-zinc-700 dark:text-zinc-300">Education</span>
              </div>
              <button @click="openNew('education')" class="text-xs font-medium text-primary dark:text-primary-300 hover:opacity-70 transition-opacity">+ Add</button>
            </div>
            <div v-if="educationEntries.length === 0 && !isLoadingEntries" class="text-sm text-zinc-400 dark:text-zinc-500 py-4">No education entries yet.</div>
            <div class="flex flex-col gap-4">
              <div v-for="entry in educationEntries" :key="entry.id" class="flex flex-col gap-2 w-full">
                <div class="flex gap-3 items-stretch w-full">
                  <ProfileEntryCard
                    entry-type="education"
                    :entry="entry"
                    @edit="openEdit('education', entry)"
                    @archive="archiveEntry('education', entry)"
                  />
                  <ProfileEntryCard
                    v-for="variant in (entry.variants || [])"
                    :key="variant.id"
                    entry-type="education"
                    :entry="variant"
                    @edit="openEdit('education', variant)"
                    @archive="archiveVariant('education', entry, variant)"
                  />
                  <button @click="openVariant('education', entry)" class="add-variant-btn">
                    <svg class="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
                    <span class="add-variant-label">add alternative</span>
                  </button>
                </div>
                <MasterReferenceSection
                  entry-type="education"
                  :entry-id="entry.id"
                  :has-variants="(entry.variants || []).length > 0"
                />
              </div>
            </div>
          </section>

          <!-- Activities -->
          <section id="section-activities" class="profile-section-anchor">
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-2">
                <span class="w-0.5 h-3.5 rounded-full bg-gold flex-shrink-0"></span>
                <span class="text-xs font-semibold uppercase tracking-widest text-zinc-700 dark:text-zinc-300">Activities</span>
              </div>
              <button @click="openNew('activity')" class="text-xs font-medium text-primary dark:text-primary-300 hover:opacity-70 transition-opacity">+ Add</button>
            </div>
            <div v-if="activityEntries.length === 0 && !isLoadingEntries" class="text-sm text-zinc-400 dark:text-zinc-500 py-4">No activity entries yet.</div>
            <div class="flex flex-col gap-6">
              <div v-for="entry in activityEntries" :key="entry.id" class="flex flex-col gap-2 w-full">
                <div class="flex gap-3 items-stretch w-full">
                  <ProfileEntryCard
                    entry-type="activity"
                    :entry="entry"
                    @edit="openEdit('activity', entry)"
                    @archive="archiveEntry('activity', entry)"
                  />
                  <ProfileEntryCard
                    v-for="variant in (entry.variants || [])"
                    :key="variant.id"
                    entry-type="activity"
                    :entry="variant"
                    @edit="openEdit('activity', variant)"
                    @archive="archiveVariant('activity', entry, variant)"
                  />
                  <button @click="openVariant('activity', entry)" class="add-variant-btn">
                    <svg class="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
                    <span class="add-variant-label">add alternative</span>
                  </button>
                </div>
                <MasterReferenceSection
                  entry-type="activity"
                  :entry-id="entry.id"
                  :has-variants="(entry.variants || []).length > 0"
                />
              </div>
            </div>
          </section>

          <!-- Skills -->
          <section id="section-skills" class="profile-section-anchor">
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-2">
                <span class="w-0.5 h-3.5 rounded-full bg-gold flex-shrink-0"></span>
                <span class="text-xs font-semibold uppercase tracking-widest text-zinc-700 dark:text-zinc-300">Skills</span>
              </div>
              <button @click="openNew('skill')" class="text-xs font-medium text-primary dark:text-primary-300 hover:opacity-70 transition-opacity">+ Add</button>
            </div>
            <div v-if="skillCategories.length === 0 && !isLoadingEntries" class="text-sm text-zinc-400 dark:text-zinc-500 py-4">No skill categories yet.</div>
            <div class="flex flex-wrap gap-4 items-start">
              <ProfileEntryCard
                v-for="skill in skillCategories"
                :key="skill.id"
                entry-type="skill"
                :entry="skill"
                @edit="openEdit('skill', skill)"
                @archive="archiveSkill(skill)"
              />
            </div>
          </section>

        </div>
      </div>
    </main>

    <!-- Entry edit/create modal -->
    <ProfileEditModal
      :is-open="isModalOpen"
      :entry-type="modalEntryType"
      :entry="modalEntry"
      :parent-entry="modalParentEntry"
      @close="closeModal"
      @saved="handleSaved"
    />

  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute, onBeforeRouteLeave } from 'vue-router'
import logoImage from '../assets/icons/cv-tool-gold.png'
import PersonalInfoForm from './PersonalInfoForm.vue'
import UnsavedChangesModal from './UnsavedChangesModal.vue'
import UserDropdown from './UserDropdown.vue'
import ProfileEntryCard from './ProfileEntryCard.vue'
import MasterReferenceSection from './MasterReferenceSection.vue'
import ProfileEditModal from './ProfileEditModal.vue'
import { useAuth } from '../composables/useAuth.js'
import { validateCvParserFile, parseCvParserError } from '../config/cvConfig.js'

const TYPE_CONFIG = {
  experience: { endpoint: '/api/master_experience_entries' },
  education:  { endpoint: '/api/master_education_entries'  },
  activity:   { endpoint: '/api/master_activities'         },
  skill:      { endpoint: '/api/master_skill_categories'   }
}

export default {
  name: 'UserProfile',
  components: { PersonalInfoForm, UnsavedChangesModal, UserDropdown, ProfileEntryCard, ProfileEditModal, MasterReferenceSection },
  setup() {
    const router = useRouter()
    // Shared logout: resets the cached auth state and redirects to the
    // landing page (a local signout + router.push leaves the router guard
    // with stale authenticated state).
    // currentUser is the account record (Devise name/email) — the topbar
    // dropdown must show this, not the editable CV personal info below.
    const { handleLogout, currentUser } = useAuth()
    const route  = useRoute()
    const isDarkMode = ref(false)

    const isSaving            = ref(false)
    const isLoadingEntries    = ref(true)
    const showWelcomeBanner   = ref(false)
    const showUploadBanner    = ref(true)
    const showUploadModal     = ref(false)
    const showUnsavedChangesModal = ref(false)
    const pendingNavigation   = ref(null)
    const successMessage      = ref('')
    const errorMessage        = ref('')
    const hasUnsavedChanges   = ref(false)

    const uploadedFile   = ref(null)
    const isUploading    = ref(false)
    const uploadProgress = ref('')

    const highlightedFields = ref({ personalInfo: [] })

    const activeSection = ref('section-personal')
    let sectionObserver = null

    const personalInfo = ref({ fullName: '', email: '', phone: '', location: '' })
    const originalPersonalInfo = ref({})

    const experienceEntries = ref([])
    const educationEntries  = ref([])
    const activityEntries   = ref([])
    const skillCategories   = ref([])

    const isModalOpen      = ref(false)
    const modalEntryType   = ref('experience')
    const modalEntry       = ref(null)
    const modalParentEntry = ref(null)

    const navItems = [
      { id: 'section-personal',   label: 'Personal',   icon: '<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>' },
      { id: 'section-experience', label: 'Experience', icon: '<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m8 0H8m8 0a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2"/></svg>' },
      { id: 'section-education',  label: 'Education',  icon: '<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"/></svg>' },
      { id: 'section-activities', label: 'Activities', icon: '<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>' },
      { id: 'section-skills',     label: 'Skills',     icon: '<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>' }
    ]

    const isProfileEmpty = computed(() =>
      experienceEntries.value.length === 0 &&
      educationEntries.value.length === 0 &&
      activityEntries.value.length === 0 &&
      skillCategories.value.length === 0 &&
      !personalInfo.value.fullName &&
      !personalInfo.value.email
    )

    const csrf = () =>
      document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    const showMessage = (msg, type = 'success') => {
      if (type === 'success') { successMessage.value = msg; errorMessage.value = '' }
      else { errorMessage.value = msg; successMessage.value = '' }
      setTimeout(() => { successMessage.value = ''; errorMessage.value = '' }, 5000)
    }

    const scrollToSection = (id) => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    async function fetchPersonalInfo() {
      try {
        const res  = await fetch('/api/users/current', { headers: { Accept: 'application/json' } })
        const data = await res.json()
        if (data.success && data.user?.personal_info) {
          const pi = data.user.personal_info
          personalInfo.value = {
            fullName: pi.full_name || '',
            email:    pi.email     || '',
            phone:    pi.phone     || '',
            location: pi.location  || ''
          }
          originalPersonalInfo.value = { ...personalInfo.value }
        }
      } catch (e) {
        showMessage('Failed to load profile data', 'error')
      }
    }

    async function fetchEntries() {
      isLoadingEntries.value = true
      try {
        const [expRes, eduRes, actRes, skillRes] = await Promise.all([
          fetch('/api/master_experience_entries', { headers: { Accept: 'application/json' } }),
          fetch('/api/master_education_entries',  { headers: { Accept: 'application/json' } }),
          fetch('/api/master_activities',          { headers: { Accept: 'application/json' } }),
          fetch('/api/master_skill_categories',    { headers: { Accept: 'application/json' } })
        ])
        const [expData, eduData, actData, skillData] = await Promise.all([
          expRes.json(), eduRes.json(), actRes.json(), skillRes.json()
        ])
        if (expData.success)   experienceEntries.value = expData.master_experience_entries || []
        if (eduData.success)   educationEntries.value  = eduData.master_education_entries  || []
        if (actData.success)   activityEntries.value   = actData.master_activities         || []
        if (skillData.success) skillCategories.value   = skillData.master_skill_categories || []
      } catch (e) {
        showMessage('Failed to load profile entries', 'error')
      } finally {
        isLoadingEntries.value = false
      }
    }

    const handlePersonalInfoChange = () => {
      hasUnsavedChanges.value = JSON.stringify(personalInfo.value) !== JSON.stringify(originalPersonalInfo.value)
    }

    // Persist the personal info; returns true on success. Navigation is the
    // caller's concern (save button → dashboard, leave-popup → pending target).
    async function persistPersonalInfo() {
      isSaving.value = true
      try {
        const res = await fetch('/api/users/update_profile', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf(), Accept: 'application/json' },
          body: JSON.stringify({
            profile_data: {
              personal_info: {
                full_name: personalInfo.value.fullName,
                email:     personalInfo.value.email,
                phone:     personalInfo.value.phone,
                location:  personalInfo.value.location
              }
            }
          })
        })
        const data = await res.json()
        if (data.success) {
          originalPersonalInfo.value = { ...personalInfo.value }
          hasUnsavedChanges.value = false
          return true
        }
        showMessage(data.message || 'Failed to save profile', 'error')
        return false
      } catch (e) {
        showMessage('Error saving profile', 'error')
        return false
      } finally {
        isSaving.value = false
      }
    }

    async function savePersonalInfo() {
      if (await persistPersonalInfo()) router.push('/dashboard')
    }

    function openNew(type)          { modalEntryType.value = type; modalEntry.value = null;  modalParentEntry.value = null;   isModalOpen.value = true }
    function openEdit(type, entry)  { modalEntryType.value = type; modalEntry.value = entry; modalParentEntry.value = null;   isModalOpen.value = true }
    function openVariant(type, par) { modalEntryType.value = type; modalEntry.value = null;  modalParentEntry.value = par;    isModalOpen.value = true }
    function closeModal()           { isModalOpen.value = false }

    function handleSaved(savedEntry) {
      const type = modalEntryType.value
      if (type === 'skill') {
        const idx = skillCategories.value.findIndex(s => s.id === savedEntry.id)
        if (idx >= 0) skillCategories.value[idx] = savedEntry
        else skillCategories.value.unshift(savedEntry)
        return
      }
      const list = type === 'experience' ? experienceEntries
                 : type === 'education'  ? educationEntries
                 : activityEntries

      if (savedEntry.parent_id) {
        const parent = list.value.find(e => e.id === savedEntry.parent_id)
        if (parent) {
          if (!parent.variants) parent.variants = []
          const vi = parent.variants.findIndex(v => v.id === savedEntry.id)
          if (vi >= 0) parent.variants[vi] = savedEntry
          else parent.variants.push(savedEntry)
        }
      } else {
        const idx = list.value.findIndex(e => e.id === savedEntry.id)
        if (idx >= 0) {
          const existing = list.value[idx]
          list.value[idx] = { ...savedEntry, variants: savedEntry.variants ?? existing.variants ?? [] }
        } else {
          list.value.unshift({ ...savedEntry, variants: [] })
        }
      }
    }

    async function archiveEntry(type, entry) {
      const { endpoint } = TYPE_CONFIG[type]
      await fetch(`${endpoint}/${entry.id}`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': csrf(), Accept: 'application/json' }
      })
      if ((entry.variants || []).length > 0) {
        // The backend promotes the oldest variant to canonical — re-fetch so
        // the surviving alternatives show up under their new parent
        await fetchEntries()
        return
      }
      const list = type === 'experience' ? experienceEntries
                 : type === 'education'  ? educationEntries
                 : activityEntries
      list.value = list.value.filter(e => e.id !== entry.id)
    }

    async function archiveVariant(type, parent, variant) {
      const { endpoint } = TYPE_CONFIG[type]
      await fetch(`${endpoint}/${variant.id}`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': csrf(), Accept: 'application/json' }
      })
      if (parent.variants) {
        parent.variants = parent.variants.filter(v => v.id !== variant.id)
      }
    }

    async function archiveSkill(skill) {
      await fetch(`/api/master_skill_categories/${skill.id}`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': csrf(), Accept: 'application/json' }
      })
      skillCategories.value = skillCategories.value.filter(s => s.id !== skill.id)
    }

    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges.value) { e.preventDefault(); e.returnValue = '' }
    }

    const hideUnsavedChangesModal = () => { showUnsavedChangesModal.value = false; pendingNavigation.value = null }
    const discardChangesAndLeave  = () => {
      // Capture the target before hideUnsavedChangesModal resets it
      const target = pendingNavigation.value
      hasUnsavedChanges.value = false
      hideUnsavedChangesModal()
      if (target) router.push(target)
    }
    const saveAndLeave = async () => {
      const target = pendingNavigation.value
      if (await persistPersonalInfo()) {
        hideUnsavedChangesModal()
        router.push(target || '/dashboard')
      }
    }

    // Intercept in-app navigation away from the profile while there are
    // unsaved changes (scoped to this component, unlike a global beforeEach)
    onBeforeRouteLeave((to) => {
      if (hasUnsavedChanges.value) {
        pendingNavigation.value = to.fullPath
        showUnsavedChangesModal.value = true
        return false
      }
      return true
    })

    const closeUploadModal   = () => { showUploadModal.value = false; uploadedFile.value = null; isUploading.value = false; uploadProgress.value = '' }
    const handleFileUpload   = (e) => { uploadedFile.value = e.target.files[0] || null }
    const handleDrop         = (e) => { e.preventDefault(); uploadedFile.value = e.dataTransfer.files[0] || null }

    async function uploadAndPopulateProfile() {
      if (!uploadedFile.value) return

      const fileError = validateCvParserFile(uploadedFile.value)
      if (fileError) {
        showMessage(fileError, 'error')
        return
      }

      isUploading.value = true
      uploadProgress.value = 'Uploading file…'
      try {
        const fd = new FormData()
        fd.append('file', uploadedFile.value)
        const res  = await fetch('/api/cv_parser/upload', { method: 'POST', headers: { Accept: 'application/json', 'X-CSRF-Token': csrf() }, body: fd })
        uploadProgress.value = 'Processing CV…'
        const data = await res.json()
        if (data.success && data.cv_data) {
          const pi = data.cv_data.personal_info || {}
          personalInfo.value = {
            fullName: pi.full_name || '',
            email:    pi.email     || '',
            phone:    pi.phone     || '',
            location: pi.location  || ''
          }
          highlightedFields.value.personalInfo = []
          if (!pi.full_name) highlightedFields.value.personalInfo.push('fullName')
          if (!pi.email)     highlightedFields.value.personalInfo.push('email')
          closeUploadModal()
          showUploadBanner.value = false
          showMessage('CV uploaded! Review highlighted fields, then save.', 'success')
          await fetchEntries()
        } else {
          throw new Error(parseCvParserError(res, data))
        }
      } catch (e) {
        showMessage(e.message || 'Failed to upload CV — please try again', 'error')
      } finally {
        isUploading.value = false
        uploadProgress.value = ''
      }
    }

    onMounted(async () => {
      const mq = window.matchMedia('(prefers-color-scheme: dark)')
      isDarkMode.value = mq.matches
      mq.addEventListener('change', (e) => { isDarkMode.value = e.matches })

      await Promise.all([fetchPersonalInfo(), fetchEntries()])

      if (route.query.welcome === '1') showWelcomeBanner.value = true

      window.addEventListener('beforeunload', handleBeforeUnload)

      sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(e => { if (e.isIntersecting) activeSection.value = e.target.id })
      }, { rootMargin: '-15% 0px -75% 0px', threshold: 0 })

      navItems.forEach(({ id }) => {
        const el = document.getElementById(id)
        if (el) sectionObserver.observe(el)
      })
    })

    onUnmounted(() => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      sectionObserver?.disconnect()
    })

    return {
      isDarkMode, logoImage, navItems, activeSection, scrollToSection,
      isSaving, isLoadingEntries, showWelcomeBanner, showUploadBanner,
      showUploadModal, showUnsavedChangesModal,
      successMessage, errorMessage, hasUnsavedChanges, isProfileEmpty,
      personalInfo, highlightedFields,
      experienceEntries, educationEntries, activityEntries, skillCategories,
      isModalOpen, modalEntryType, modalEntry, modalParentEntry,
      uploadedFile, isUploading, uploadProgress,
      handlePersonalInfoChange, savePersonalInfo, handleLogout, currentUser,
      openNew, openEdit, openVariant, closeModal, handleSaved,
      archiveEntry, archiveVariant, archiveSkill,
      hideUnsavedChangesModal, discardChangesAndLeave, saveAndLeave,
      closeUploadModal, handleFileUpload, handleDrop, uploadAndPopulateProfile
    }
  }
}
</script>

<style scoped>
.profile-section-anchor {
  scroll-margin-top: 5rem;
}

.add-variant-btn {
  @apply flex-shrink-0 w-12 self-stretch border border-zinc-200 dark:border-zinc-700 rounded-xl flex flex-col items-center justify-center gap-1.5 text-zinc-300 dark:text-zinc-600 hover:border-gold hover:text-gold hover:bg-gold-50/50 dark:hover:border-gold-600 dark:hover:text-gold-400 transition-colors cursor-pointer bg-transparent;
}

.add-variant-label {
  font-size: 14px;
  line-height: 1.2;
  letter-spacing: 0.03em;
  writing-mode: vertical-lr;
  @apply text-current font-medium;
}
</style>
